//frontend/src/features/rrhh/empleados/api/empleadosApi.js
import httpClient from "../../../../shared/api/httpClient";
import { qs } from "../../../../shared/api/qs";

// ─── helpers ───────────────────────────────────────────────
function getLicenseIdOrThrow() {
  try {
    const v = localStorage.getItem("sabi_empresa_id");
    if (!v) throw new Error();
    const n = Number(v);
    if (!Number.isFinite(n) || n <= 0) throw new Error();
    return n;
  } catch {
    throw new Error("No se encontró sabi_empresa_id en localStorage (licencia/empresa requerida).");
  }
}

// ─── Enum mappings (frontend label → backend enum) ─────────
const MARITAL_STATUS_MAP = {
  soltero: "SINGLE",
  casado: "MARRIED",
  divorciado: "DIVORCED",
  viudo: "WIDOWED",
  union_de_hecho: "UNION",
  otro: "OTHER",
  SINGLE: "SINGLE", MARRIED: "MARRIED", DIVORCED: "DIVORCED",
  WIDOWED: "WIDOWED", UNION: "UNION", OTHER: "OTHER",
};

const EDUCATION_LEVEL_MAP = {
  sin_estudios: "NONE",
  primario: "PRIMARY",
  secundario: "SECONDARY",
  terciario: "TERTIARY",
  universitario: "UNIVERSITY",
  posgrado: "POSTGRAD",
  otro: "OTHER",
  NONE: "NONE", PRIMARY: "PRIMARY", SECONDARY: "SECONDARY",
  TERTIARY: "TERTIARY", UNIVERSITY: "UNIVERSITY", POSTGRAD: "POSTGRAD", OTHER: "OTHER",
};

const EMPLOYEE_STATUS_MAP = {
  activo: "ACTIVE",
  inactivo: "INACTIVE",
  suspendido: "INACTIVE",
  despedido: "INACTIVE",
  renuncia: "INACTIVE",
  ACTIVE: "ACTIVE", INACTIVE: "INACTIVE",
};

function mapEnum(value, map) {
  if (!value) return undefined;
  return map[String(value)] || map[String(value).toLowerCase()] || undefined;
}

// ─── EMPLOYEES (hr/administration) ────────────────────────

/**
 * Listar empleados con paginación
 * GET /hr/administration/employees?page=1&limit=50
 */
export const getAllEmpleados = async (filters = {}) => {
  const query = qs({
    page: filters.page ?? 1,
    limit: filters.limit ?? 50,
    search: filters.search || undefined,
    status: filters.status || undefined,
    contractName: filters.contractName || undefined,
    branchName: filters.branchName || undefined,
    areaName: filters.areaName || undefined,
    roleName: filters.roleName || undefined,
  });

  return httpClient.get(`/hr/administration/employees${query}`, {
    signal: filters.signal,
  });
};

/**
 * Obtener un empleado por ID (ficha)
 * GET /hr/administration/employees/:id
 */
export const getEmpleadoFicha = async (id) => {
  if (!id) throw new Error("El ID del empleado es requerido");
  return httpClient.get(`/hr/administration/employees/${id}`);
};

/**
 * Crear empleado
 * POST /hr/administration/employees
 */
export const createEmpleado = async (empleadoData) => {
  if (!empleadoData) throw new Error("empleadoData es requerido");
  const licenseId = getLicenseIdOrThrow();

  const branchIds = Array.isArray(empleadoData.branchIds)
    ? empleadoData.branchIds.map(Number).filter(Boolean)
    : Array.isArray(empleadoData.branch_ids)
    ? empleadoData.branch_ids.map(Number).filter(Boolean)
    : [];

  const areaIds = Array.isArray(empleadoData.areaIds)
    ? empleadoData.areaIds.map(Number).filter(Boolean)
    : Array.isArray(empleadoData.area_ids)
    ? empleadoData.area_ids.map(Number).filter(Boolean)
    : [];

  const roleIds = Array.isArray(empleadoData.roleIds)
    ? empleadoData.roleIds.map(Number).filter(Boolean)
    : Array.isArray(empleadoData.role_ids)
    ? empleadoData.role_ids.map(Number).filter(Boolean)
    : [];

  if (!branchIds.length) throw new Error("Debe seleccionar al menos una sucursal");
  if (!areaIds.length) throw new Error("Debe seleccionar al menos un área");
  if (!roleIds.length) throw new Error("Debe seleccionar al menos un rol");

  const fullName = (
    empleadoData.fullName || empleadoData.full_name || empleadoData.nombre || ""
  ).trim();
  const dni = (empleadoData.dni || empleadoData.documento || "").trim();
  const email = (empleadoData.email || "").trim();

  if (!fullName) throw new Error("Nombre completo es requerido");
  if (!dni) throw new Error("DNI es requerido");
  if (!email) throw new Error("Email es requerido");

  const rawChildren = empleadoData.childrenCount ?? empleadoData.children_count ?? empleadoData.hijos;
  const childrenCount = rawChildren != null && rawChildren !== "" ? Number(rawChildren) : undefined;

  const rawSalary = empleadoData.salaryDecimal || empleadoData.salary || empleadoData.salario;
  const salaryDecimal =
    rawSalary != null && rawSalary !== "" && rawSalary !== 0
      ? String(rawSalary)
      : undefined;

  const payload = {
    licenseId,
    branchIds,
    areaIds,
    roleIds,
    fullName,
    dni,
    email,
    cuit: empleadoData.cuit || undefined,
    birthDate: empleadoData.birthDate || empleadoData.birth_date || empleadoData.fecha_nacimiento || undefined,
    maritalStatus: mapEnum(
      empleadoData.maritalStatus || empleadoData.marital_status || empleadoData.estado_civil,
      MARITAL_STATUS_MAP
    ),
    childrenCount: Number.isFinite(childrenCount) ? childrenCount : undefined,
    educationLevel: mapEnum(
      empleadoData.educationLevel || empleadoData.education_level || empleadoData.nivel_educacion,
      EDUCATION_LEVEL_MAP
    ),
    health: empleadoData.health || undefined,
    phoneNumber: empleadoData.phoneNumber || empleadoData.phone_number || empleadoData.telefono || undefined,
    nationality: empleadoData.nationality || undefined,
    province: empleadoData.province || undefined,
    locality: empleadoData.locality || undefined,
    neighborhood: empleadoData.neighborhood || undefined,
    address: empleadoData.address || empleadoData.direccion || undefined,
    postalCode: empleadoData.postalCode || empleadoData.postal_code || empleadoData.codigo_postal || undefined,
    hireDate: empleadoData.hireDate || empleadoData.hire_date || empleadoData.fecha_ingreso || undefined,
    clothing: empleadoData.clothing || undefined,
    contractName: empleadoData.contractName || empleadoData.contract_name || empleadoData.tipo_contrato || undefined,
    status: mapEnum(
      empleadoData.status || empleadoData.estado,
      EMPLOYEE_STATUS_MAP
    ),
    contractEndDate: empleadoData.contractEndDate || empleadoData.contract_end_date || empleadoData.fecha_fin_contrato || undefined,
    salaryDecimal,
    contractPdfId: empleadoData.contractPdfId || undefined,
    profilePhotoId: empleadoData.profilePhotoId || undefined,
    // ─── Perfil de liquidación ───
    salaryMode: empleadoData.salaryMode || undefined,
    baseSalary: empleadoData.baseSalary != null && empleadoData.baseSalary !== "" ? String(empleadoData.baseSalary) : undefined,
    hourlyRate: empleadoData.hourlyRate != null && empleadoData.hourlyRate !== "" ? String(empleadoData.hourlyRate) : undefined,
    cvu: empleadoData.cvu || undefined,
    bank: empleadoData.bank || undefined,
    accountAlias: empleadoData.accountAlias || undefined,
    payrollNotes: empleadoData.payrollNotes || undefined,
  };

  Object.keys(payload).forEach((k) => {
    if (payload[k] === undefined || payload[k] === "") delete payload[k];
  });

  return httpClient.post(`/hr/administration/employees`, payload);
};

/**
 * Actualizar empleado parcialmente
 * PATCH /hr/administration/employees/:id
 */
export const patchEmpleado = async (id, data) => {
  if (!id) throw new Error("El ID del empleado es requerido");
  if (!data) throw new Error("data es requerido");

  const payload = {};

  const fieldMap = {
    fullName: "fullName",
    full_name: "fullName",
    nombre: "fullName",
    email: "email",

    dni: "dni",
    documento: "dni",

    cuit: "cuit",

    birthDate: "birthDate",
    birth_date: "birthDate",
    fecha_nacimiento: "birthDate",

    maritalStatus: "maritalStatus",
    marital_status: "maritalStatus",
    estado_civil: "maritalStatus",

    childrenCount: "childrenCount",
    children_count: "childrenCount",
    hijos: "childrenCount",

    educationLevel: "educationLevel",
    education_level: "educationLevel",
    nivel_educacion: "educationLevel",

    health: "health",
    salud: "health",

    phoneNumber: "phoneNumber",
    phone_number: "phoneNumber",
    telefono: "phoneNumber",

    nationality: "nationality",
    nacionalidad: "nationality",

    province: "province",
    provincia: "province",

    locality: "locality",
    localidad: "locality",

    neighborhood: "neighborhood",
    barrio: "neighborhood",

    address: "address",
    direccion: "address",

    postalCode: "postalCode",
    postal_code: "postalCode",
    codigo_postal: "postalCode",

    hireDate: "hireDate",
    hire_date: "hireDate",
    fecha_ingreso: "hireDate",

    clothing: "clothing",
    indumentaria: "clothing",

    contractName: "contractName",
    contract_name: "contractName",
    tipo_contrato: "contractName",

    status: "status",
    estado: "status",

    contractEndDate: "contractEndDate",
    contract_end_date: "contractEndDate",
    fecha_fin_contrato: "contractEndDate",

    salaryDecimal: "salaryDecimal",
    salary: "salaryDecimal",
    salario: "salaryDecimal",

    contractPdfId: "contractPdfId",
    profilePhotoId: "profilePhotoId",
    licenseId: "licenseId",
    // ─── Perfil de liquidación ───
    salaryMode: "salaryMode",
    salary_mode: "salaryMode",
    baseSalary: "baseSalary",
    base_salary: "baseSalary",
    hourlyRate: "hourlyRate",
    hourly_rate: "hourlyRate",
    cvu: "cvu",
    payroll_cvu: "cvu",
    bank: "bank",
    payroll_bank: "bank",
    accountAlias: "accountAlias",
    payroll_account_alias: "accountAlias",
    payrollNotes: "payrollNotes",
    payroll_notes: "payrollNotes",
  };

  const enumFields = {
    maritalStatus: MARITAL_STATUS_MAP,
    educationLevel: EDUCATION_LEVEL_MAP,
    status: EMPLOYEE_STATUS_MAP,
    salaryMode: { MENSUAL: "MENSUAL", HORA: "HORA", mensual: "MENSUAL", hora: "HORA" },
  };

  const numberFields = new Set(["childrenCount", "licenseId"]);
  const decimalFields = new Set(["salaryDecimal", "baseSalary", "hourlyRate"]);
  const dateFields = new Set(["birthDate", "hireDate", "contractEndDate"]);

  if (Array.isArray(data.branchIds) || Array.isArray(data.branch_ids)) {
    const branchIds = (data.branchIds || data.branch_ids || []).map(Number).filter(Boolean);
    if (branchIds.length) payload.branchIds = branchIds;
  }

  if (Array.isArray(data.areaIds) || Array.isArray(data.area_ids)) {
    const areaIds = (data.areaIds || data.area_ids || []).map(Number).filter(Boolean);
    if (areaIds.length) payload.areaIds = areaIds;
  }

  if (Array.isArray(data.roleIds) || Array.isArray(data.role_ids)) {
    const roleIds = (data.roleIds || data.role_ids || []).map(Number).filter(Boolean);
    if (roleIds.length) payload.roleIds = roleIds;
  }

  Object.entries(data).forEach(([key, value]) => {
    const backendKey = fieldMap[key];
    if (!backendKey) return;

    if (value === undefined || value === null || value === "") return;

    if (enumFields[backendKey]) {
      const mapped = mapEnum(value, enumFields[backendKey]);
      if (mapped) payload[backendKey] = mapped;
    } else if (decimalFields.has(backendKey)) {
      payload[backendKey] = String(value);
    } else if (numberFields.has(backendKey)) {
      const n = Number(value);
      if (Number.isFinite(n)) payload[backendKey] = n;
    } else if (dateFields.has(backendKey)) {
      if (/^\d{4}-\d{2}-\d{2}/.test(String(value))) {
        payload[backendKey] = String(value).slice(0, 10);
      }
    } else {
      payload[backendKey] = value;
    }
  });

  if (Object.keys(payload).length === 0) {
    throw new Error("No hay campos para actualizar.");
  }

  return httpClient.patch(`/hr/administration/employees/${id}`, payload);
};

/**
 * Desactivar (soft delete) empleado
 * DELETE /hr/administration/employees/:id
 */
export const deleteEmpleado = async (id) => {
  if (!id) throw new Error("El ID del empleado es requerido");
  return httpClient.delete(`/hr/administration/employees/${id}`);
};

/**
 * Obtener sucursales de la licencia activa
 * GET /admin/licenses/:licenseId/branches
 */
export const getSucursales = async () => {
  const licenseId = getLicenseIdOrThrow();
  return httpClient.get(`/admin/licenses/${licenseId}/branches`);
};

export const previewImportEmpleadosCsv = async (file) => {
  const licenseId = getLicenseIdOrThrow();

  const formData = new FormData();
  formData.append("file", file);
  formData.append("licenseId", String(licenseId));

  return httpClient.post(`/hr/administration/employees/import/preview`, formData);
};

export const importEmpleadosCsv = async (file) => {
  const licenseId = getLicenseIdOrThrow();

  const formData = new FormData();
  formData.append("file", file);
  formData.append("licenseId", String(licenseId));

  return httpClient.post(`/hr/administration/employees/import`, formData);
};
